import {
  CleanPageDataResponse,
  ProjectData,
  SqBotData
} from '@DStypes/project';
import { createSlice } from '@reduxjs/toolkit';

const initialState: {
  data?: { [key: string]: any };
  configs?: any[];
  currentProjectData?: ProjectData;
  currentProjectFieldList?: any;
  combineProjectFieldList?: any;
  cleanPageData?: CleanPageDataResponse;
  sqBotData: SqBotData;
  dropDownData?: any;
} = {
  sqBotData: {
    message: '',
    sqb_id: '',
    chart_id: '',
    action: '',
    button_text: '',
    prompt: '',
    rating: 0,
    draftPrompt: '',
    usedButton: false
  }
};

export const projectBoard = createSlice({
  name: 'projectBoard',
  initialState,
  reducers: {
    updateChartDataById: (state, { payload }) => {
      const data = { ...state.data, [payload.id]: payload.data };

      return { ...state, data };
    },
    resetChartDataArray: (state) => {
      const data = {};

      return { ...state, data };
    },
    updateConfigs: (state, { payload }) => ({ ...state, configs: payload }),
    removeChartById: (state, { payload }) => {
      const prevData = { ...state.data };

      delete prevData[payload.id];

      const newConfigs = state.configs?.filter(
        (config) => config.id !== payload.id
      );

      return {
        ...state,
        configs: newConfigs,
        data: prevData,
        currentProjectData: state.currentProjectData
          ? {
              ...state.currentProjectData,
              chart_id_order: state.currentProjectData.chart_id_order.filter(
                (item) => item !== payload.id
              )
            }
          : undefined
      };
    },
    updateChartIdOrder: (state, { payload }) => {
      const prevData = { ...state.data };

      delete prevData[payload.id];

      return {
        ...state,
        data: prevData,
        currentProjectData: state.currentProjectData
          ? {
              ...state.currentProjectData,
              chart_id_order: payload
            }
          : undefined
      };
    },
    removeChartSByIdARR: (state, { payload }) => {
      const prevData = { ...state.data };

      payload.idArr.forEach((item: string) => delete prevData[item]);

      const newConfigs = state.configs?.filter(
        (config) => !payload.idArr.includes(config.id)
      );

      return {
        ...state,
        configs: newConfigs,
        data: prevData,
        currentProjectData: state.currentProjectData
          ? {
              ...state.currentProjectData,
              chart_id_order: state.currentProjectData.chart_id_order.filter(
                (item) => !payload.idArr.includes(item)
              )
            }
          : undefined
      };
    },
    resetConfigs: (state) => ({ ...state, configs: undefined }),
    setCurrentProjectData: (state, { payload }) => ({
      ...state,
      currentProjectData: { ...state.currentProjectData, ...payload }
    }),
    setCurrentProjectFields: (state, { payload }) => ({
      ...state,
      currentProjectFieldList: {
        ...payload
      }
    }),
    setProjectFieldImportStatus: (state, { payload }) => ({
      ...state,
      currentProjectFieldList: {
        ...state.currentProjectFieldList,
        fields: state.currentProjectFieldList.fields.map((field: any) => {
          if (field.id === payload.id)
            return { ...field, to_import: payload.importStatus };

          return field;
        })
      }
    }),
    setProjectFieldType: (state, { payload }) => ({
      ...state,
      currentProjectFieldList: {
        ...state.currentProjectFieldList,
        fields: state.currentProjectFieldList.fields.map((field: any) => {
          if (field.id === payload.id)
            return { ...field, treat_as_type: payload.type };

          return field;
        })
      }
    }),
    setProjectFieldName: (state, { payload }) => ({
      ...state,
      currentProjectFieldList: {
        ...state.currentProjectFieldList,
        fields: state.currentProjectFieldList.fields.map((field: any) => {
          if (field.id === payload.id)
            return { ...field, fieldname: payload.value };

          return field;
        })
      }
    }),
    setProjectAnonymisation: (state, { payload }) => ({
      ...state,
      currentProjectFieldList: {
        ...state.currentProjectFieldList,
        fields: state.currentProjectFieldList.fields.map((field: any) => {
          if (field.id === payload.id)
            return {
              ...field,
              treat_as_non_sensitive: payload.treat_as_non_sensitive
            };

          return field;
        })
      }
    }),
    setCombineProjectFields: (state, { payload }) => ({
      ...state,
      combineProjectFieldList: {
        ...payload
      }
    }),
    setCombineProjectFieldImportStatus: (state, { payload }) => ({
      ...state,
      combineProjectFieldList: {
        ...state.combineProjectFieldList,
        fields: state.combineProjectFieldList.fields.map((field: any) => {
          if (field.id === payload.id)
            return { ...field, to_import: payload.importStatus };

          return field;
        })
      }
    }),
    setCombineProjectFieldType: (state, { payload }) => ({
      ...state,
      combineProjectFieldList: {
        ...state.combineProjectFieldList,
        fields: state.combineProjectFieldList.fields.map((field: any) => {
          if (field.id === payload.id)
            return { ...field, treat_as_type: payload.type };

          return field;
        })
      }
    }),
    setCombineProjectFieldName: (state, { payload }) => ({
      ...state,
      combineProjectFieldList: {
        ...state.combineProjectFieldList,
        fields: state.combineProjectFieldList.fields.map((field: any) => {
          if (field.id === payload.id)
            return { ...field, fieldname: payload.value };

          return field;
        })
      }
    }),
    setCleanPageData: (state, { payload }) => ({
      ...state,
      cleanPageData: {
        ...payload
      }
    }),
    setSqBotData: (state, { payload }) => ({
      ...state,
      sqBotData: {
        ...state.sqBotData,
        ...payload
      }
    }),
    resetSqBotData: (state) => ({
      ...state,
      sqBotData: {
        message: '',
        sqb_id: '',
        chart_id: '',
        action: '',
        button_text: '',
        prompt: '',
        rating: 0,
        draftPrompt: '',
        usedButton: false
      }
    }),
    setDropDownData: (state, { payload }) => ({
      ...state,
      dropDownData: payload
    })
  }
});

export const {
  updateChartDataById,
  removeChartById,
  removeChartSByIdARR,
  updateConfigs,
  resetConfigs,
  setCurrentProjectData,
  setCurrentProjectFields,
  setProjectFieldImportStatus,
  setProjectFieldType,
  setProjectFieldName,
  setProjectAnonymisation,
  setCombineProjectFields,
  setCombineProjectFieldImportStatus,
  setCombineProjectFieldType,
  setCombineProjectFieldName,
  updateChartIdOrder,
  resetChartDataArray,
  setCleanPageData,
  setSqBotData,
  resetSqBotData,
  setDropDownData
} = projectBoard.actions;

export default projectBoard.reducer;
